/**
 * Mirrors change-log writes to the database.
 *
 * WHY A REGISTRY: saveHistory() is a plain module function called from a
 * dozen places across the three hooks. Threading an adapter down to each call
 * site would mean touching all of them and carrying a ref through every
 * mutation callback. Instead the hooks register a pusher once, and
 * saveHistory consults it -- one integration point rather than a dozen.
 *
 * Every push is FIRE-AND-FORGET. A failed audit-log sync must never block or
 * fail the edit that produced it; localStorage already holds the entry, so
 * the worst case is a log that is briefly behind.
 */

import type { Backend } from "./types"
import { makeHistoryAdapter, makeConversationAdapter } from "./historyAdapter"

type Pusher = (entries: any[]) => void

const pushers = new Map<Backend, Pusher>()

/** Debounce per backend: a bulk import writes history once per entity. */
const timers = new Map<Backend, ReturnType<typeof setTimeout>>()
const latest = new Map<Backend, any[]>()
/*
 * The change log is appended in bursts -- a bulk import writes one entry per
 * entity -- so it keeps a slightly wider window than the entity stores. Still
 * short enough that the log is current by the time anyone opens it.
 */
const DEBOUNCE_MS = 300

export function registerHistorySync(backend: Backend, teamId: string | null): void {
  const existing = timers.get(backend)
  if (existing) { clearTimeout(existing); timers.delete(backend) }
  latest.delete(backend)

  if (!teamId) { pushers.delete(backend); return }

  const adapter = makeHistoryAdapter(backend, teamId)
  pushers.set(backend, entries => {
    latest.set(backend, entries)
    const t = timers.get(backend)
    if (t) clearTimeout(t)
    timers.set(backend, setTimeout(() => {
      const payload = latest.get(backend)
      latest.delete(backend)
      timers.delete(backend)
      if (!payload?.length) return
      void adapter.save(payload).catch(err => {
        // Deliberately swallowed: the entry is already in localStorage, and
        // failing the user's edit over an audit-log write would be worse.
        console.error(`[smart-cms] history sync failed (${backend}):`, err)
      })
    }, DEBOUNCE_MS))
  })
}

/** Called by each backend's saveHistory(). No-op when unregistered. */
export function pushHistory(backend: Backend, entries: any[]): void {
  pushers.get(backend)?.(entries)
}

/** Reads the log from the database, or null when this backend is local-only. */
export async function pullHistory(backend: Backend, teamId: string): Promise<any[] | null> {
  if (!pushers.has(backend)) return null
  try {
    return await makeHistoryAdapter(backend, teamId).load()
  } catch (err) {
    console.error(`[smart-cms] history load failed (${backend}):`, err)
    return null
  }
}


/* ── Conversations ──────────────────────────────────────────────────────── */

/**
 * The app names backends "cms" / "services" / "siteconfig" (see ChatBackend),
 * but the scms_conversation.backend CHECK constraint uses the canonical
 * "articles" / "service-map" / "site-config". Without this mapping every
 * conversation insert is rejected by the database.
 */
const CONVERSATION_BACKEND: Record<string, string> = {
  cms: "articles",
  services: "service-map",
  siteconfig: "site-config",
}

/** Canonical DB value for an app-side backend name. */
function dbBackend(backend: string): string {
  return CONVERSATION_BACKEND[backend] ?? backend
}


type ConvPusher = {
  save: (c: any) => void
  remove: (id: string) => void
}

const convPushers = new Map<string, ConvPusher>()

export function registerConversationSync(backend: string, teamId: string | null): void {
  if (!teamId) { convPushers.delete(backend); return }
  const adapter = makeConversationAdapter(dbBackend(backend), teamId)
  convPushers.set(backend, {
    // Fire-and-forget, same reasoning as history: a conversation that fails
    // to sync is already in localStorage, and failing the user's turn over it
    // would be worse than being briefly out of date.
    save: c => void adapter.save(c).catch(err =>
      console.error(`[smart-cms] conversation sync failed (${backend}):`, err)),
    remove: id => void adapter.remove(id).catch(err =>
      console.error(`[smart-cms] conversation delete failed (${backend}):`, err)),
  })
}

export function pushConversation(backend: string, conversation: any): void {
  convPushers.get(backend)?.save(conversation)
}

export function pushConversationDelete(backend: string, id: string): void {
  convPushers.get(backend)?.remove(id)
}

/** Reads conversations from the database, or null when this backend is local-only. */
export async function pullConversations(backend: string, teamId: string): Promise<any[] | null> {
  if (!convPushers.has(backend)) return null
  try {
    return await makeConversationAdapter(dbBackend(backend), teamId).list()
  } catch (err) {
    console.error(`[smart-cms] conversation load failed (${backend}):`, err)
    return null
  }
}
